import createApiClient from '../../../utils/createApiClient'
import getTranslationsQuery from './getTranslationsQuery'
import { logError } from '../../../utils/logging'
import transformTranslationsMetaobjectData from './transformTranslationsMetaobjectData'
import getShopifyGraphQLUpperLocale from "./getShopifyGraphQLUpperLocale"

type MetaObjectFields = Array<{
  key: string
  value: string
}>

type MetaObjectNode = {
  fields: MetaObjectFields
}

type MetaObjectsQueryData = {
  metaobjects: {
    edges: Array<{
      node: MetaObjectNode
    }>
  }
}

type MetaObjectQueryData = {
  metaobject: {
    fields: MetaObjectFields
  }
}

const getGlobalId = (metaobjectId: number) => `gid://shopify/Metaobject/${metaobjectId}`

/**
 * Fetch metaobject data by type and locale
 */
const getTranslationsMetaobject = async (
  token: string,
  metaObjectType: string,
): Promise<Record<string, string> | null> => {
  const storefrontApiClient = createApiClient(token)

  if (!storefrontApiClient) {
    return null
  }

  const locale = getShopifyGraphQLUpperLocale()
  const query = getTranslationsQuery(metaObjectType, locale)

  try {
    const { data, errors } = await storefrontApiClient.request(query)

    if (errors) {
      logError(`Storefront API failed when fetching metaobject by type, using fallback: ${errors}`)
      return null
    }

    const metaobjectsData = data as MetaObjectsQueryData

    if (!metaobjectsData.metaobjects?.edges?.length) {
      return null
    }

    return transformTranslationsMetaobjectData(metaobjectsData.metaobjects.edges[0].node.fields)
  } catch (error) {
    logError(`Error fetching metaobject by type ${metaObjectType}: ${error}`)
    return null
  }
}

/**
 * Fetch metaobject data by specific ID
 */
const getTranslationsMetaobjectById = async (
  token: string,
  metaobjectId: number,
): Promise<Record<string, string> | null> => {
  const storefrontApiClient = createApiClient(token)

  if (!storefrontApiClient) {
    return null
  }

  const locale = getShopifyGraphQLUpperLocale()
  const gid = getGlobalId(metaobjectId)
  const query = `
    query getTranslationsMetaobjectById @inContext(country: ${window.Shopify?.country}, language: ${locale}) {
      metaobject(id: "${gid}") {
        fields {
          key
          value
        }
      }
    }
  `

  try {
    const { data, errors } = await storefrontApiClient.request(query)

    if (errors) {
      logError(`Storefront API failed when fetching metaobject by ID, using fallback: ${errors}`)
      return null
    }

    const metaobjectData = data as MetaObjectQueryData

    if (!metaobjectData.metaobject?.fields) {
      logError(`Metaobject with GID ${gid} not found or has no fields`)
      return null
    }

    return transformTranslationsMetaobjectData(metaobjectData.metaobject.fields)
  } catch (error) {
    logError(`Error fetching metaobject by GID ${gid}: ${error}`)
    return null
  }
}

export { getTranslationsMetaobject, getTranslationsMetaobjectById }
export default getTranslationsMetaobject
